import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';

import type { MegaNavItem } from '../../types';
import { DashboardNavItem } from '../DashboardNavItem';
import GlassMegaNavFlyout from './GlassMegaNavFlyout';
import { GlassNavLink } from './GlassNavLink';

interface GlassMegaNavProps {
  slug: string;
  megaNavData?: MegaNavItem[];
  onOpenChange?: (itemName: string | null) => void;
}

export const GlassMegaNav: React.FC<GlassMegaNavProps> = ({ slug, megaNavData, onOpenChange }) => {
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const handleToggle = useCallback(
    (itemName: string) => {
      const next = openNavItem === itemName ? null : itemName;
      if (next !== null) {
        openerRef.current = document.activeElement as HTMLElement;
      } else {
        openerRef.current = null;
      }
      setOpenNavItem(next);
      onOpenChange?.(next);
    },
    [openNavItem, onOpenChange],
  );

  const handleClose = useCallback(() => {
    setOpenNavItem(null);
    onOpenChange?.(null);
    openerRef.current?.focus();
    openerRef.current = null;
  }, [onOpenChange]);

  useEffect(() => {
    if (!openNavItem) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openNavItem, handleClose]);

  const activeItem = useMemo(
    () => megaNavData?.find((item) => item.name === openNavItem && item.sections?.length),
    [megaNavData, openNavItem],
  );

  return (
    // `mr-8` keeps a 32px gap between the last nav item (Dashboard, when signed in)
    // and the account avatar in the right-aligned glass nav.
    <Box ref={navRef} className="relative col-start-2 col-end-3 row-start-1 row-end-2 mr-8 block">
      <Row
        role="list"
        analyticsName="cosmos-glass-nav-menu-list"
        className="m-0 w-full list-none items-center justify-end gap-1.5 max-md:hidden"
      >
        {megaNavData?.map((navItem) => {
          const hasSections = navItem.sections && navItem.sections.length > 0;
          const isOpen = openNavItem === navItem.name;

          return (
            <Box role="listitem" key={navItem.name} className="relative block">
              <GlassNavLink
                name={navItem.name}
                href={navItem.href}
                hasDropdown={hasSections}
                isOpen={isOpen}
                active={slug === navItem.href}
                onPress={hasSections ? () => handleToggle(navItem.name) : undefined}
              />
            </Box>
          );
        })}
        <DashboardNavItem slug={slug} variant="glass" />
      </Row>
      <GlassMegaNavFlyout item={activeItem ?? null} />
    </Box>
  );
};
